<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import type { Ref } from 'vue'
// TODO figure out why it inherits Error from @stripe and remove this type
type NuxtErr = {
  url: string
  statusCode: number
  statusMessage: string
  message: string
  description: string
  data?: any
}
type GlobalError = Ref<Error | NuxtErr | null | undefined>
const error: GlobalError = useError()
const { t } = useI18n()
const handleError = () => {
  clearError({
    redirect: '/',
  })
}
const errorMessage = computed(() => {
  return error && error.value && error.value.message
    ? typeof t !== 'undefined' && error.value.message === error.value.message.toUpperCase()
      ? t(error.value.message)
      : error.value.message
    : ''
})
const colorScheme = computed(() => {
  const data = (error.value as NuxtErr).data
  return data && data.colorScheme
    ? data.colorScheme
    : null
})
const logo = computed(() => {
  const data = (error.value as NuxtErr).data
  if (!(data && data.logo && data.logo.type)) {
    return null
  }
  const logo = data.logo
  const altText = logo.altText ? logo.altText : data.name ? data.name : undefined
  const def = { altText, src: logo.original ? logo.original : undefined, type: logo.type }
  return !logo.original
    ? data.name
    : !logo.sizes
        ? def
        : logo.type === 'square'
          ? logo.sizes.s64
            ? { altText, src: logo.sizes.s64, type: logo.type }
            : def
          : logo.sizes.landscapeLogo
            ? { altText, src: logo.sizes.landscapeLogo, type: logo.type }
            : def
}) as Record<string, any>
const hideButton = computed(() => {
  const data = (error.value as NuxtErr).data
  const statusCode = (error.value as NuxtErr).statusCode
  if (
    !(statusCode) ||
    (statusCode === 503) ||
    (data && !data.id)
  ) {
    return true
  }
  return false
})
</script>

<template>
  <NuxtLayout name="default">
    <main class="grid h-screen place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8">
      <div class="text-center">
        <div
          v-if="logo"
          class="w-full text-center inline-flex items-center justify-center mb-5"
        >
          <h1 v-if="!logo.src">
            {{ logo.altText }}
          </h1>
          <img
            v-else
            :src="logo.src"
            :alt="logo.altText"
            :class="{ 'h-16 w-16': logo.type === 'square', 'h-12': logo.type !== 'square' }"
          >
        </div>
        <div v-if="error && (error as NuxtErr).statusCode">
          <p
            :class="{ 'text-blue-600': !colorScheme }"
            class="text-base font-semibold"
            :style="[colorScheme ? { color: colorScheme.accentColor } : undefined]"
          >
            {{ (error as NuxtErr).statusCode }}
          </p>
          <h1 class="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            {{ errorMessage }}
          </h1>
          <p
            v-if="(error as NuxtErr).statusCode === 404"
            class="mt-6 text-base leading-7 text-gray-600"
          >
            {{ t('PAGE_NOT_FOUND_DESCRIPTION') }}
          </p>
          <p
            v-else-if="(error as NuxtErr).statusCode === 503"
            class="mt-6 text-base leading-7 text-gray-600"
          >
            {{ t('PAGE_COMING_SOON_DESCRIPTION') }}
          </p>
        </div>
        <div
          v-if="!hideButton"
          :class="{ 'mt-10': error && (error as NuxtErr).statusCode }"
          class="flex items-center justify-center gap-x-6"
        >
          <button
            type="button"
            :class="{ 'bg-blue-600 text-white': !colorScheme }"
            class="tw-button"
            :style="[colorScheme
              ? { 'background-color': colorScheme.accentColor, 'color': colorScheme.accentContrastColor }
              : undefined]"
            @click="handleError"
          >
            &lsaquo; {{ t('BACK_TO_HOME') }}
          </button>
        </div>
      </div>
    </main>
  </NuxtLayout>
</template>

<style scoped>

</style>
